/**
 * AI generation in the platform: what the API, the worker and the web agree
 * on. The source is `docs/ai-generation/02-contract.md` (sections named
 * below); where this file and the contract disagree, the contract wins until
 * it is changed.
 *
 * A Generate click is a **run**; each provider call it makes is an
 * **output** (the worker's queue row and the ledger's line). An output is not
 * a take until a person adopts it. Prices live here so the browser shows the
 * same number the server checks against the team's cap.
 */
import type { UndoCallBlocker } from "./storyboards.js";

/* ------------------------------------------------------------------ models */

/** Every voiceover, on every line, is Eleven v4. There is no model picker. */
export const ELEVEN_TTS_MODEL = "eleven_v4" as const;
/** Nano Banana Pro, through the Gemini API. */
export const FRAME_MODEL = "gemini-3-pro-image" as const;
/** Fixed for every frame: 9:16 at 2K, which Gemini renders at 1536 x 2752. */
export const FRAME_ASPECT_RATIO = "9:16" as const;
export const FRAME_IMAGE_SIZE = "2K" as const;
export const FRAME_PIXELS = { width: 1536, height: 2752 } as const;

export const CLIP_MODELS = ["seedance_2_5", "seedance_2_0"] as const;
export type ClipModel = (typeof CLIP_MODELS)[number];
export const CLIP_RESOLUTIONS = ["720p", "1080p"] as const;
export type ClipResolution = (typeof CLIP_RESOLUTIONS)[number];

/** A clip model's default resolution, duration range and reference cap (start frame included). Both default to 720p; 1080p is a choice. */
export const CLIP_MODEL_SPECS: Record<ClipModel, { resolution: ClipResolution; minS: number; maxS: number; maxInputs: number }> = {
  seedance_2_5: { resolution: "720p", minS: 4, maxS: 30, maxInputs: 30 },
  seedance_2_0: { resolution: "720p", minS: 4, maxS: 15, maxInputs: 9 },
};

/**
 * A clip made before anybody chose otherwise, everywhere (the composers, the
 * prompt chat's cards, a create request that names no model): Seedance 2.0
 * at 720p. It is 35% cheaper than 2.5 and needed no more retries; 1080p cost
 * 2.3-2.5 times as much with no sign that it helped.
 */
export const CLIP_DEFAULT: { model: ClipModel; resolution: ClipResolution } = { model: "seedance_2_0", resolution: "720p" };

/** What a clip may be sent beside its pictures: reference videos and reference audio (Seedance's `video_urls`, `audio_urls`). */
export const CLIP_MEDIA_KINDS = ["video", "audio"] as const;
export type ClipMediaKind = (typeof CLIP_MEDIA_KINDS)[number];

/**
 * Higgsfield's reference-to-video limits per model (docs.higgsfield.ai, models/seedance-2-5 and
 * seedance-2 reference-to-video): 2.5 takes 30 images, 10 videos and 10 audio files, 50 in all, each
 * media type normalized to 30 s in total; 2.0 takes 9, 3 and 3, every video trimmed to 15 s, the audio
 * to 15 s in total, and audio alone is not enough.
 */
export const CLIP_MEDIA_LIMITS: Record<ClipModel, { video: number; audio: number; total: number; videoEachS: number; videoTotalS: number; audioTotalS: number; audioAlone: boolean }> = {
  seedance_2_5: { video: 10, audio: 10, total: 50, videoEachS: 30, videoTotalS: 30, audioTotalS: 30, audioAlone: true },
  seedance_2_0: { video: 3, audio: 3, total: 15, videoEachS: 15, videoTotalS: 45, audioTotalS: 15, audioAlone: false },
};

/** The resolutions each clip model may be asked for; the spec's own `resolution` is its default. */
export const CLIP_MODEL_RESOLUTIONS: Record<ClipModel, readonly ClipResolution[]> = {
  seedance_2_5: ["720p", "1080p"],
  seedance_2_0: ["720p", "1080p"],
};

export const clipResolutionAllowed = (model: ClipModel, resolution: ClipResolution): boolean => CLIP_MODEL_RESOLUTIONS[model]?.includes(resolution) ?? false;

/** The resolution to keep when the model changes: the same one when the model takes it, else the nearest it does. */
export function snapClipResolution(model: ClipModel, resolution: ClipResolution): ClipResolution {
  const allowed = CLIP_MODEL_RESOLUTIONS[model];
  if (allowed.includes(resolution)) return resolution;
  const at = CLIP_RESOLUTIONS.indexOf(resolution);
  return [...allowed].sort((a, b) => Math.abs(CLIP_RESOLUTIONS.indexOf(a) - at) - Math.abs(CLIP_RESOLUTIONS.indexOf(b) - at))[0] ?? CLIP_MODEL_SPECS[model].resolution;
}

/** Pixels of a 9:16 clip at each resolution, for the token estimate. */
export const CLIP_PIXELS: Record<ClipResolution, { width: number; height: number }> = {
  "720p": { width: 720, height: 1280 },
  "1080p": { width: 1080, height: 1920 },
};

/* ------------------------------------------------------------- runs, outputs */

/** `read` is Voiceover B (the whole script); `stitch` is Voiceover A, created only by its rebuild route. */
export const GENERATION_KINDS = ["frame", "clip", "voiceover", "read", "stitch"] as const;
export type GenerationKind = (typeof GENERATION_KINDS)[number];

export const GENERATION_PROVIDERS = ["elevenlabs", "gemini", "higgsfield", "upload", "ffmpeg"] as const;
export type GenerationProvider = (typeof GENERATION_PROVIDERS)[number];
/** The providers that cost money: only their runs count against the cap and appear on the Spend page. */
export const PAID_GENERATION_PROVIDERS: readonly GenerationProvider[] = ["elevenlabs", "gemini", "higgsfield"];

/** Who makes each kind when it is generated rather than uploaded. */
export const GENERATION_PROVIDER_OF: Record<GenerationKind, GenerationProvider> = {
  frame: "gemini",
  clip: "higgsfield",
  voiceover: "elevenlabs",
  read: "elevenlabs",
  stitch: "ffmpeg",
};

/** `fake` and `replay` outputs carry a "Test render" badge wherever they are drawn. */
export const GENERATION_MODES = ["live", "fake", "replay"] as const;
export type GenerationMode = (typeof GENERATION_MODES)[number];

/** Outputs per click: 3 frames, 2 voiceover takes, 1 read, 1 clip, 1 stitch; an upload is 1. */
export const GENERATION_COUNTS: Record<GenerationKind, number> = { frame: 3, voiceover: 2, read: 1, clip: 1, stitch: 1 };
export const UPLOAD_COUNT = 1;
/** Frames per click, chosen in the composer: 1-4, three unless asked otherwise. */
export const FRAME_COUNT_RANGE = { min: 1, max: 4, default: 3 } as const;

export const GENERATION_OUTPUT_STATUSES = ["queued", "sending", "running", "storing", "done", "failed", "unknown", "cancelled"] as const;
export type GenerationOutputStatus = (typeof GENERATION_OUTPUT_STATUSES)[number];
/** The states an output never leaves on its own (an `unknown` clip moves only on a recheck). */
export const TERMINAL_OUTPUT_STATUSES: readonly GenerationOutputStatus[] = ["done", "failed", "unknown", "cancelled"];
export const isTerminalOutput = (status: GenerationOutputStatus): boolean => TERMINAL_OUTPUT_STATUSES.includes(status);

/** A run's status, derived from its outputs and never stored. */
export type GenerationRunStatus = "running" | "done" | "partial" | "failed" | "cancelled";

/**
 * Running while any output still moves; otherwise done, partial, failed or
 * cancelled. An extra image never decides it, and a retried output is
 * replaced by its retry.
 */
export function runStatus(
  outputs: ReadonlyArray<{ id?: number; status: GenerationOutputStatus; extra?: boolean; retryOf?: number | null }>,
): GenerationRunStatus {
  if (outputs.some((o) => !isTerminalOutput(o.status))) return "running";
  const replaced = new Set(outputs.map((o) => o.retryOf).filter((id): id is number => typeof id === "number"));
  const counted = outputs.filter((o) => !o.extra && (o.id === undefined || !replaced.has(o.id)));
  const done = counted.filter((o) => o.status === "done").length;
  if (counted.length > 0 && done === counted.length) return "done";
  if (done > 0) return "partial";
  if (counted.length > 0 && counted.every((o) => o.status === "cancelled")) return "cancelled";
  return "failed";
}

export const COST_SOURCES = ["reported", "estimated", "none"] as const;
export type CostSource = (typeof COST_SOURCES)[number];

/** Higgsfield's own view of a clip it has accepted. */
export type ProviderClipStatus = "queued" | "in_progress";

/* ------------------------------------------------------- errors and words */

/** Why an output failed (contract 6.3). `backpressure` is a status while waiting, never terminal. */
export const GENERATION_ERROR_CLASSES = [
  "safety",
  "no_image",
  "bad_request",
  "bad_reference",
  "credits",
  "voice",
  "provider_down",
  "timeout",
  "crashed",
  "backpressure",
] as const;
export type GenerationErrorClass = (typeof GENERATION_ERROR_CLASSES)[number];

/** What the web says about an output's state, and whether it may have cost money. */
export interface GenerationStateWords {
  /** The i18n key of the sentence (plan 2.11). Placeholders: `{provider}`, `{words}`, `{n}`. */
  key: string;
  charged: "no" | "maybe";
}

export const GENERATION_STATE_WORDS: Record<GenerationErrorClass | "unknown" | "cancelled", GenerationStateWords> = {
  cancelled: { key: "generation.state.cancelled", charged: "no" },
  unknown: { key: "generation.state.unknown", charged: "maybe" },
  backpressure: { key: "generation.state.backpressure", charged: "no" },
  safety: { key: "generation.state.safety", charged: "no" },
  no_image: { key: "generation.state.no_image", charged: "no" },
  bad_request: { key: "generation.state.bad_request", charged: "no" },
  bad_reference: { key: "generation.state.bad_reference", charged: "no" },
  credits: { key: "generation.state.credits", charged: "no" },
  voice: { key: "generation.state.voice", charged: "no" },
  // "maybe": a provider that answered 200 with something unreadable was paid (the row's actual_usd says what).
  provider_down: { key: "generation.state.provider_down", charged: "maybe" },
  timeout: { key: "generation.state.timeout", charged: "maybe" },
  crashed: { key: "generation.state.crashed", charged: "maybe" },
};

/** The error codes generation routes answer with, in the storyboard vocabulary `{ error, code, hint }`. */
export const GENERATION_ERROR_CODES = [
  "generation_disabled",
  "cap_reached",
  "too_many_running",
  "invalid_tag",
  "already_rendering",
  "stale_stitch",
  "last_option",
  "main_frame",
  "hide_current",
  "undo_conflict",
  /** An input that names no stored picture (`n` says which). */
  "invalid_input",
  /** A voice not in the team's list, or archived. */
  "invalid_voice",
  /** The slot or line an adopt aimed at was archived meanwhile: reopen the target popover. */
  "target_gone",
  /** A retry of an `unknown` clip, which may already have been charged, sent without `confirmPayTwice`. */
  "confirm_required",
  /** A frame or clip sent with `checkRepeat` that is identical to an earlier run on the shot (`RepeatRequestDetails`). */
  "repeat_request",
] as const;
export type GenerationErrorCode = (typeof GENERATION_ERROR_CODES)[number];

/* ------------------------------------------------------------------ limits */

/** Contract 6.2. Inputs counts include a clip's start frame. */
export const GENERATION_LIMITS = {
  inputs: { frame: 14, seedance_2_5: 30, seedance_2_0: 9 },
  /** A quiet warning above these: more than 5 product pictures or 8 in all on a frame; more than 6 on a clip. */
  inputWarning: { frameProduct: 5, frameTotal: 8, clip: 6 },
  /** Characters, tags included: one take, and Voiceover B's whole read. */
  characters: { voiceover: 10_000, read: 9_900 },
  /** Pictures per frame run. */
  frameCount: { min: FRAME_COUNT_RANGE.min, max: FRAME_COUNT_RANGE.max },
  /** Runs in flight per shot or line (frame, clip, voiceover) and per storyboard (read, stitch). */
  inFlight: { frame: 3, clip: 2, voiceover: 3, read: 1, stitch: 1 },
} as const;

/** The most inputs a run of this kind (and clip model) may attach. */
export function maxInputs(kind: GenerationKind, clipModel?: ClipModel): number {
  if (kind === "frame") return GENERATION_LIMITS.inputs.frame;
  if (kind === "clip") return GENERATION_LIMITS.inputs[clipModel ?? "seedance_2_5"];
  return 0;
}

/** Characters as ElevenLabs counts them: code points, tags included. */
export const characterCount = (text: string): number => Array.from(text).length;

/* ------------------------------------------------------------------- money */

/** The team's monthly cap until an admin changes it (a Europe/Rome calendar month; it blocks). */
export const DEFAULT_MONTHLY_CAP_USD = 1500;
/** Above this share of the cap, a quiet line under Generate. */
export const CAP_WARNING_SHARE = 0.8;
export const SPEND_TIMEZONE = "Europe/Rome";

/** One row of the shared price table. An admin may set a measured rate over it (`PriceOverrides`). */
export interface PriceRow {
  kind: GenerationKind;
  model: string;
  /** What `rate` is per. */
  unit: "1k_characters" | "image" | "1k_tokens";
  /** Dollars per unit. */
  rate: number;
  /** Not yet measured: priced at the dearest known rate until the smoke test measures it. */
  assumed: boolean;
}

export const PRICE_ROW_IDS = [
  "voiceover:eleven_v4",
  "read:eleven_v4",
  "frame:gemini-3-pro-image",
  "clip:seedance_2_5:720p",
  "clip:seedance_2_5:1080p",
  "clip:seedance_2_0:720p",
  "clip:seedance_2_0:1080p",
] as const;
export type PriceRowId = (typeof PRICE_ROW_IDS)[number];

/** Contract 6.1, list prices. */
export const PRICE_TABLE: Record<PriceRowId, PriceRow> = {
  // $0.08 per 1K characters, the list price (a 72% promotion until 2026-10-12 is not encoded).
  "voiceover:eleven_v4": { kind: "voiceover", model: ELEVEN_TTS_MODEL, unit: "1k_characters", rate: 0.08, assumed: false },
  "read:eleven_v4": { kind: "read", model: ELEVEN_TTS_MODEL, unit: "1k_characters", rate: 0.08, assumed: false },
  // Per image; the per-reference, per-prompt-token and per-call parts are FRAME_PRICE_PARTS.
  "frame:gemini-3-pro-image": { kind: "frame", model: FRAME_MODEL, unit: "image", rate: 0.1344, assumed: false },
  // Per 1K tokens, as Higgsfield's `/estimate` states them: Seedance 2.5 $0.0214 at 720p (about $0.46 a
  // second) and $0.0234 at 1080p (about $1.14 a second); Seedance 2.0 $0.014 at both.
  "clip:seedance_2_5:720p": { kind: "clip", model: "seedance_2_5", unit: "1k_tokens", rate: 0.0214, assumed: false },
  "clip:seedance_2_5:1080p": { kind: "clip", model: "seedance_2_5", unit: "1k_tokens", rate: 0.0234, assumed: false },
  "clip:seedance_2_0:720p": { kind: "clip", model: "seedance_2_0", unit: "1k_tokens", rate: 0.014, assumed: false },
  "clip:seedance_2_0:1080p": { kind: "clip", model: "seedance_2_0", unit: "1k_tokens", rate: 0.014, assumed: false },
};

/** The rest of a frame's price, per output: each attached image, each prompt token, and a fixed part. */
export const FRAME_PRICE_PARTS = { perReference: 0.0011, perPromptToken: 0.000002, perCall: 0.0048 } as const;

/** An admin's measured rates, by price row; `generation_settings.price_overrides`. */
export type PriceOverrides = Partial<Record<PriceRowId, number>>;

/** The rate in force for a row: the override when an admin set one. A `read` follows the voiceover rate unless it has its own. */
export function priceRate(id: PriceRowId, overrides: PriceOverrides = {}): number {
  const own = overrides[id];
  if (typeof own === "number" && Number.isFinite(own) && own >= 0) return own;
  if (id === "read:eleven_v4") return priceRate("voiceover:eleven_v4", overrides);
  return PRICE_TABLE[id].rate;
}

/** Dollars to six places, the ledger's precision. */
export const roundUsd = (usd: number): number => Math.round(usd * 1e6) / 1e6;

/** What a click is about to cost, shown as "about" and checked against the cap. */
export interface GenerationEstimate {
  row: PriceRowId | null;
  /** Characters, images or tokens, for the whole run. */
  units: number;
  usd: number;
  /** One output's share, for Retry this one and for the ledger's `est_usd`. */
  perOutput: { units: number; usd: number };
  assumed: boolean;
}

const free = (): GenerationEstimate => ({ row: null, units: 0, usd: 0, perOutput: { units: 0, usd: 0 }, assumed: false });

/** Two takes (or `count`) of `text`: characters x count x the rate. Tags count as characters. */
export function estimateVoiceover(text: string, count: number = GENERATION_COUNTS.voiceover, overrides?: PriceOverrides): GenerationEstimate {
  const chars = characterCount(text);
  const each = roundUsd((chars / 1000) * priceRate("voiceover:eleven_v4", overrides));
  return { row: "voiceover:eleven_v4", units: chars * count, usd: roundUsd(each * count), perOutput: { units: chars, usd: each }, assumed: false };
}

/** Voiceover B, one take of the whole script. */
export function estimateRead(text: string, overrides?: PriceOverrides): GenerationEstimate {
  const chars = characterCount(text);
  const usd = roundUsd((chars / 1000) * priceRate("read:eleven_v4", overrides));
  return { row: "read:eleven_v4", units: chars, usd, perOutput: { units: chars, usd }, assumed: false };
}

/** About four characters to a token: the prompt's share of a frame's price is small either way. */
export const promptTokens = (prompt: string): number => Math.ceil(characterCount(prompt) / 4);

/** n x (image + refs x per-reference + prompt tokens x per-token + per-call); about $0.44 for three. */
export function estimateFrame(prompt: string, references: number, count: number = GENERATION_COUNTS.frame, overrides?: PriceOverrides): GenerationEstimate {
  const each = roundUsd(
    priceRate("frame:gemini-3-pro-image", overrides) +
      references * FRAME_PRICE_PARTS.perReference +
      promptTokens(prompt) * FRAME_PRICE_PARTS.perPromptToken +
      FRAME_PRICE_PARTS.perCall,
  );
  return { row: "frame:gemini-3-pro-image", units: count, usd: roundUsd(each * count), perOutput: { units: 1, usd: each }, assumed: false };
}

/** Seedance tokens: ceil(seconds x width x height x 24 / 1024). */
export function clipTokens(durationS: number, resolution: ClipResolution): number {
  const { width, height } = CLIP_PIXELS[resolution];
  return Math.ceil((durationS * width * height * 24) / 1024);
}

/**
 * The seconds of reference video Higgsfield bills as input ("input video seconds + generated video
 * seconds"; image and audio references are free): each video trimmed and the total normalized as
 * the model does. A video whose length is not known counts as the longest it could be.
 */
export function clipInputVideoSeconds(model: ClipModel, media: ReadonlyArray<Pick<GenerationMediaInput, "kind" | "durationMs">> | undefined): number {
  const limits = CLIP_MEDIA_LIMITS[model];
  let total = 0;
  for (const m of media ?? []) {
    if (m.kind !== "video") continue;
    const s = typeof m.durationMs === "number" && m.durationMs > 0 ? m.durationMs / 1000 : limits.videoEachS;
    total += Math.min(limits.videoEachS, s);
  }
  return Math.min(limits.videoTotalS, Math.ceil(total));
}

/** One clip; about $2.30 for 5 s at 720p on 2.5. Reference video seconds are billed as well. */
export function estimateClip(model: ClipModel, resolution: ClipResolution, durationS: number, overrides?: PriceOverrides, inputVideoS = 0): GenerationEstimate {
  const row = `clip:${model}:${resolution}` as PriceRowId;
  const tokens = clipTokens(durationS + Math.max(0, inputVideoS), resolution);
  const usd = roundUsd((tokens / 1000) * priceRate(row, overrides));
  return { row, units: tokens, usd, perOutput: { units: tokens, usd }, assumed: PRICE_TABLE[row].assumed };
}

/** The estimate for a run request of any kind; a stitch and an upload are free. */
export function estimateRequest(kind: GenerationKind, request: GenerationRequest, overrides?: PriceOverrides): GenerationEstimate {
  switch (kind) {
    case "voiceover": {
      const r = request as VoiceoverRequest;
      return estimateVoiceover(r.text, r.seeds?.length || GENERATION_COUNTS.voiceover, overrides);
    }
    case "read":
      return estimateRead((request as ReadRequest).text, overrides);
    case "frame": {
      const r = request as FrameRequest;
      return estimateFrame(r.prompt, r.inputs.length, r.count ?? GENERATION_COUNTS.frame, overrides);
    }
    case "clip": {
      const r = request as ClipRequest;
      return estimateClip(r.model, r.resolution, r.durationS, overrides, clipInputVideoSeconds(r.model, r.media));
    }
    default:
      return free();
  }
}

/* ---------------------------------------------------- the request snapshot */

/** Where an input's bytes came from. */
export const GENERATION_INPUT_SOURCES = ["take", "library", "upload", "ref_image", "output"] as const;
export type GenerationInputSource = (typeof GENERATION_INPUT_SOURCES)[number];

/**
 * One picture sent with a frame or clip, pinned by sha256, in `n` order (the
 * `@Image n` it answers to). `key` is the slot ref for a `take` or
 * `ref_image` (`s07/frame/v2`, `ref_image/tin-closed-topdown-studio`), the
 * library key for `library`, the sha256 for an `upload`, and the output id
 * for an `output` (a finished frame of a run on this storyboard that is not
 * on any slot: the reference picker's generated frames).
 */
export interface GenerationInput {
  n: number;
  role: "image_reference" | "start_image";
  source: GenerationInputSource;
  key: string;
  sha256: string;
  storageKey: string;
  /** The ASSET MAP line for this picture, as the prompt says it. */
  note?: string;
}

/**
 * Where a reference video or audio comes from: a take of this storyboard (by take id), a finished output (by output id),
 * a file uploaded into a prompt (by sha), or a product-library video (by library key).
 */
export const GENERATION_MEDIA_SOURCES = ["take", "output", "upload", "library"] as const;
export type GenerationMediaSource = (typeof GENERATION_MEDIA_SOURCES)[number];

/**
 * One reference video or audio file sent with a clip, pinned by sha256, in
 * `n` order within its kind: `@Video n` / `@Audio n` in the prompt, the
 * `n`th entry of Seedance's `video_urls` / `audio_urls`.
 */
export interface GenerationMediaInput {
  kind: ClipMediaKind;
  n: number;
  source: GenerationMediaSource;
  /** The take id, the output id, the upload's sha256, or the library key. */
  key: string;
  sha256: string;
  storageKey: string;
  contentType: string;
  /** Its length when known; billing counts a reference video's seconds. */
  durationMs: number | null;
  /** How a person names it: "Shot 3 · clip", "Line 2 · voiceover", a file name. */
  name?: string;
  /** What the ASSET MAP says it is for. */
  note?: string;
}

/** One reference video or audio as the browser names it; the server resolves it, numbers it within its kind and pins the storage key. */
export type GenerationMediaInputRef = Pick<GenerationMediaInput, "kind" | "source" | "key" | "sha256" | "name" | "note"> & { durationMs?: number | null };

/**
 * The surface of a generation made from the clip editor's prompt chat. Only
 * such a run may belong to the storyboard itself, with no shot (prompt-chat
 * DESIGN §3.5, D6); it is created by the server's internal door, never by the
 * public create route.
 */
export const PROMPT_CHAT_SURFACE = "prompt_chat" as const;

/**
 * A product-library reference as the prompt chat names it: `image:library:<key>`,
 * `video:library:<key>`, or `library:<key>` (the row's own kind decides). Null
 * for anything else. The server resolves and pins it (`resolveLibraryRef`).
 */
export function parseLibraryRef(ref: string): { kind: LibraryMediaKind | null; key: string } | null {
  const m = /^(?:(image|video):)?library:([A-Za-z0-9][A-Za-z0-9._-]{0,199})$/.exec(typeof ref === "string" ? ref.trim() : "");
  if (!m) return null;
  return { kind: (m[1] as LibraryMediaKind | undefined) ?? null, key: m[2]! };
}

/** Contract 3, `frame`. The prompt goes on the wire verbatim, the images after it in `n` order. */
export interface FrameRequest {
  prompt: string;
  /** What the person typed, for the recipe panel. */
  authored: string;
  inputs: GenerationInput[];
  model: typeof FRAME_MODEL;
  aspectRatio: typeof FRAME_ASPECT_RATIO;
  imageSize: typeof FRAME_IMAGE_SIZE;
  /** Pictures asked for (1-4); runs made before the choice existed have none and asked for 3. */
  count?: number;
}

/** Contract 3, `clip`. The start frame is input 1 (The shot unless another frame was picked); null for a clip sent without one. */
export interface ClipRequest {
  prompt: string;
  authored: string;
  /** `slotId` is the frame slot it came from; null for a generated frame on no slot (then `inputs[0]` names its output). */
  startFrame: { slotId: number | null; sha256: string; storageKey: string } | null;
  inputs: GenerationInput[];
  model: ClipModel;
  durationS: number;
  resolution: ClipResolution;
  aspectRatio: "9:16";
  generateAudio: boolean;
  /** Reference videos and audio, each kind numbered from 1 (`@Video n`, `@Audio n`); absent on runs made before they existed. */
  media?: GenerationMediaInput[];
}

/**
 * What a voiceover is sent with on v4: stability and similarity only, never
 * style, speed or speaker boost. `tempo` is applied afterwards with ffmpeg.
 */
export interface VoiceoverSettingsV4 {
  stability: number;
  similarity: number;
  language?: string;
  tempo?: number;
}

/** Three named points on the stability slider. */
export const STABILITY_POINTS = { creative: 0, natural: 0.5, robust: 1 } as const;
export const DEFAULT_VOICE_SETTINGS: VoiceoverSettingsV4 = { stability: STABILITY_POINTS.natural, similarity: 0.75 };

/** Contract 3, `voiceover`: one output per seed; the neighbouring lines' words go as context. */
export interface VoiceoverRequest {
  /** Exactly as sent, `[tags]` and CAPS included. */
  text: string;
  voiceId: string;
  voiceAlias: string;
  model: typeof ELEVEN_TTS_MODEL;
  settings: VoiceoverSettingsV4;
  seeds: number[];
  context?: { previous: string | null; next: string | null };
  /** A line of a re-voice (`revoice.ts`): the batch it belongs to. */
  revoice?: { batchId: number };
}

/** Contract 3, `read` (Voiceover B): the whole script, one take, no context. */
export interface ReadRequest {
  text: string;
  /** Each ordinary line's words at the click, for "read before line N changed". */
  lines: Array<{ key: string; text: string }>;
  voiceId: string;
  voiceAlias: string;
  model: typeof ELEVEN_TTS_MODEL;
  settings: VoiceoverSettingsV4;
  seeds: [number];
  /** Voiceover B read again by a re-voice (`revoice.ts`): the batch it belongs to. */
  revoice?: { batchId: number };
}

/** Contract 3, `stitch` (Voiceover A): each ordinary line's primary current take at the Rebuild click, in order. */
export interface StitchRequest {
  lines: Array<{ key: string; takeId: number; sha256: string }>;
  /**
   * In step with the shots (2026-10-13): where each line's take plays on the main cut and for how long (its trim),
   * and the cut's length; the worker lays them out with silence between. Absent on stitches made before: joined
   * end to end.
   */
  timed?: { totalMs: number; lines: Array<{ key: string; startMs: number; inMs: number; playMs: number }> };
}

/** An upload run: the composer's prompt and inputs at the moment of upload, so an adopted upload keeps its recipe. */
export interface UploadRequest {
  prompt?: string;
  inputs?: GenerationInput[];
  name: string;
  sha256: string;
}

export type GenerationRequest = FrameRequest | ClipRequest | VoiceoverRequest | ReadRequest | StitchRequest | UploadRequest;

/* ---------------------------------------------------------- what is sent */

/** One input as the browser names it; the server resolves and pins the storage key. */
export type GenerationInputRef = Pick<GenerationInput, "source" | "key" | "sha256" | "note">;

/** `POST /storyboards/:id/generations` (contract 2.1). `shot` and `line` are keys; neither for a `read`. */
export interface GenerationCreateInput {
  idempotencyKey: string;
  kind: Exclude<GenerationKind, "stitch">;
  shot?: string;
  line?: string;
  fromSlotId?: number;
  prompt?: string;
  authored?: string;
  inputs?: GenerationInputRef[];
  /** A clip's reference videos and audio, in order within each kind (`@Video 1`, `@Audio 1`, ...). */
  media?: GenerationMediaInputRef[];
  settings?: {
    /** frame: pictures in the run, 1-4 (3 when left out) */
    count?: number;
    /** clip */
    model?: ClipModel;
    /** clip: one of the model's `CLIP_MODEL_RESOLUTIONS` (its spec's resolution when left out) */
    resolution?: ClipResolution;
    durationS?: number;
    generateAudio?: boolean;
    /** voiceover, read */
    text?: string;
    voiceAlias?: string;
    stability?: number;
    similarity?: number;
    language?: string;
    tempo?: number;
  };
  /**
   * Bytes already stored through `PUT .../uploads/:sha256`: an upload run with one done output, no spend gate.
   * The size and length are what the browser measured, optional (the worker's derivatives fill them otherwise).
   */
  upload?: { sha256: string; name: string; width?: number; height?: number; durationMs?: number };
  /**
   * Accepted and no longer needed (clip editor, D12): a clip on an
   * images-only presentation always switches it to images and video (one
   * revision, in the transaction that queues the clip), flag or not.
   */
  switchToVideo?: boolean;
  /**
   * A clip's start frame, when it is not the shot's main frame (clip editor,
   * D11): a ready frame take of this same shot, so reopening a past clip's
   * prompt starts from the frame that clip started from. Left out, the clip
   * starts on the main frame's current picture, as always.
   */
  startFrameTakeId?: number;
  /**
   * A clip's start frame picked in the reference picker: any frame of this
   * presentation - a frame take of any shot (`take`, its slot ref) or a
   * generated frame on no slot (`output`, its output id) - pinned by sha.
   * Sent as input 1 with role `start_image`; wins over `startFrameTakeId`.
   */
  startFrameInput?: GenerationInputRef;
  /**
   * A clip with no start frame (its start frame removed in the prompt box):
   * every input is a reference, from `@Image 1`, and at least one is needed.
   */
  noStartFrame?: boolean;
  /**
   * Ask the server to refuse a frame or clip identical to an earlier run on
   * the same shot (same prompt, model, resolution, length, sound, pictures
   * and reference media; `repeat-request.ts`) with `409 repeat_request`,
   * whose `details` name that run (`RepeatRequestDetails`). The composers
   * send it, show the confirm, and send again without it on "Generate
   * anyway". Left out, nothing is compared (scripts and pipelines re-roll
   * on purpose). An earlier run whose every output failed or was cancelled
   * never counts: resubmitting after a platform failure is the fix.
   */
  checkRepeat?: boolean;
  /**
   * A voiceover (generated or uploaded) for one voiceover slot (clip editor,
   * free voiceovers): a free clip's slot (`StoryboardVoiceClip.slot.id`;
   * send no `line`), or a line's own voiceover slot (send its `line` too).
   * The run's outputs are adopted into that slot by
   * `POST .../voice-slots/:slotId/adopt` (`GenerationAdoptToSlotInput`).
   */
  slotId?: number;
}

/* ----------------------------------------------------------- what is read */

/** A shot or line key; a free voiceover clip's key (`voice_clip`); the storyboard itself for a `read` or `stitch`. */
export interface GenerationOwner {
  kind: "shot" | "line" | "voice_clip" | "storyboard";
  key: string | null;
}

export interface GenerationOutputView {
  id: number;
  n: number;
  extra: boolean;
  retryOf: number | null;
  status: GenerationOutputStatus;
  errorClass: GenerationErrorClass | null;
  /** The provider's own words, behind "Details". */
  error: string | null;
  providerStatus: ProviderClipStatus | null;
  cancelRequestedAt: string | null;
  cancelRefusedAt: string | null;
  sha256: string | null;
  contentType: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  estUsd: number | null;
  actualUsd: number | null;
  costSource: CostSource;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface GenerationRunView {
  id: number;
  kind: GenerationKind;
  provider: GenerationProvider;
  model: string | null;
  mode: GenerationMode;
  owner: GenerationOwner;
  fromSlotId: number | null;
  request: GenerationRequest;
  count: number;
  status: GenerationRunStatus;
  /** `role` is the account's role now (`editor`, `strategist`, `admin`), for "Mine / Everyone" and the editor's own runs. */
  requestedBy: { userId: number; name: string | null; role?: string | null };
  createdAt: string;
  outputs: GenerationOutputView[];
  /** Outputs done whose bytes are not a take and not hidden. */
  unadoptedCount: number;
  /** Made from the clip editor's prompt chat (`surface` `prompt_chat`): the "from chat" badge. */
  fromChat?: boolean;
}

/** `GET /storyboards/:id/generations`: the one read the web polls. */
export interface GenerationListResult {
  runs: GenerationRunView[];
  /** Median milliseconds of recent finished outputs, for "usually ~40 s". */
  typicalMs: { frame: number | null; clip: number | null; voiceover: number | null };
}

/** `GET /storyboards/:id/generations?unadopted=7d`: the "3 new" badges. */
export interface GenerationUnadoptedResult {
  owners: Array<{ owner: GenerationOwner; kind: GenerationKind; unadoptedCount: number }>;
}

export interface GenerationCreateResult {
  run: GenerationRunView;
  outputs: GenerationOutputView[];
  estimate: GenerationEstimate;
  /** Set when this clip switched an images-only presentation to images and video (D12): reread the storyboard. */
  switchedToVideo?: true;
}

export interface GenerationCancelResult {
  cancelled: number[];
  cancelling: number[];
}

/** `GET /generation/budget`, read before the click. */
export interface GenerationBudget {
  enabled: boolean;
  mode: GenerationMode;
  month: { spentUsd: number; capUsd: number; resetsAt: string };
  blocked: null | "disabled" | "monthly_cap";
}

/* ------------------------------------------------------------------ gallery */

export const GALLERY_GROUPS = ["run", "agent", "removed", "earlier"] as const;
export type GalleryGroup = (typeof GALLERY_GROUPS)[number];

/** One picture, clip or take in a shot's, line's or Voiceover B's gallery. */
export interface GalleryItem {
  group: GalleryGroup;
  /** Set for an output (group `run`). */
  runId: number | null;
  outputId: number | null;
  /** Set for a take (groups `agent`, `removed`, `earlier`). */
  takeId: number | null;
  slotId: number | null;
  sha256: string | null;
  contentType: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** The live slots whose current take has these bytes. */
  onSlot: number[];
  hidden: boolean;
  mode: GenerationMode | null;
  createdAt: string;
  /** frame, clip or voiceover (`read` for Voiceover B). */
  kind?: string;
  /** Who made it: the run's person, or the person whose pick made the take; null for the agent's takes. */
  by?: GalleryAuthor | null;
  /** The prompt that made it (a frame's or clip's; null for a voiceover), and the model. */
  prompt?: string | null;
  model?: string | null;
  /** An output of a run made from the prompt chat: the "from chat" badge. */
  fromChat?: boolean;
}

/** A gallery item's author, for the Mine / Everyone filter. `role` is `agent` for the agent's own takes. */
export interface GalleryAuthor {
  userId: number | null;
  name: string | null;
  role: string | null;
}

export interface GalleryResult {
  runs: GenerationRunView[];
  items: GalleryItem[];
}

/**
 * One picture in the reference picker's Frames tab (`GET /storyboards/:id/frames`):
 * a shot's frame - on a slot, an earlier take or a generated output - named
 * the way a prompt sends it (`source` + `key`, pinned by `sha256`).
 */
export interface ConceptFrame {
  /** `t<takeId>` or `o<outputId>`. */
  id: string;
  sha256: string;
  /** `take` (key: the slot ref, `s07/frame/v2`) or `output` (key: the output id). */
  source: "take" | "output";
  key: string;
  /** Set for a take: what a clip's `startFrameTakeId` names. */
  takeId: number | null;
  outputId: number | null;
  /** On the shot now: its main frame, or option N (2, 3...). Null for the rest. */
  place: "main" | "option" | null;
  option: number | null;
  createdAt: string;
  by: GalleryAuthor | null;
  thumbUrl: string;
  url: string;
  prompt: string | null;
}

export interface ConceptFramesShot {
  key: string;
  /** The shot's line; null for a shot under no line. */
  line: string | null;
  /** The main frame, its options, then every other picture of the shot, newest first. */
  frames: ConceptFrame[];
  /**
   * A combined shot's sources, in order (`shot-combine.ts`): each one's main frame as it was combined, sent as
   * its take on the combined shot's `source_frame` slot; `n` is the "Shot N" it was. Absent on any other shot.
   */
  sources?: Array<{ key: string; n: number | null; frame: ConceptFrame }>;
}

/** `GET /storyboards/:id/frames[?mine=1]`: every live shot's frames, in reading order. */
export interface ConceptFramesResult {
  storyboardId: number;
  shots: ConceptFramesShot[];
  /**
   * "From chat, not on a shot": frames the prompt chat made for the concept
   * with no target shot (`source` `output`), newest first. Never on a slot
   * until someone uses one on a shot.
   */
  fromChat: ConceptFrame[];
}

/* -------------------------------------------------------------------- voices */

/** The alias reserved for the uploaded-audio voice slot. */
export const UPLOAD_VOICE_ALIAS = "upload";

/** What checking a voice id against our ElevenLabs account answers (contract 5). */
export const VOICE_CHECK_CODES = ["found", "not_in_account", "not_a_voice_id", "key_problem", "elevenlabs_unavailable", "duplicate"] as const;
export type VoiceCheckCode = (typeof VOICE_CHECK_CODES)[number];

/** The sentence for each answer; nothing is saved unless it is `found`. */
export const VOICE_CHECK_WORDS: Record<VoiceCheckCode, { key: string; saves: boolean }> = {
  found: { key: "generation.voice.found", saves: true },
  not_in_account: { key: "generation.voice.not_in_account", saves: false },
  not_a_voice_id: { key: "generation.voice.not_a_voice_id", saves: false },
  key_problem: { key: "generation.voice.key_problem", saves: false },
  elevenlabs_unavailable: { key: "generation.voice.elevenlabs_unavailable", saves: false },
  duplicate: { key: "generation.voice.duplicate", saves: false },
};
/** Shown beside `found` for a Voice Library voice: it may cost more and can disappear. */
export const VOICE_LIBRARY_WARNING_KEY = "generation.voice.library_warning";

export interface GenerationVoice {
  id: number;
  voiceId: string;
  alias: string;
  name: string;
  note: string | null;
  labels: Record<string, string>;
  category: string | null;
  previewUrl: string | null;
  addedBy: { userId: number | null; name: string | null };
  addedAt: string;
  updatedAt: string;
  lastCheckedAt: string | null;
  lastStatus: VoiceCheckCode | null;
  archivedAt: string | null;
}

/* ---------------------------------------------------------- product library */

export const LIBRARY_ROLES = ["identity", "scale", "pellet", "angle", "lifestyle"] as const;
export type LibraryRole = (typeof LIBRARY_ROLES)[number];
export const LIBRARY_LABEL_TEXTS = ["premium", "whitening", "none"] as const;
export type LibraryLabelText = (typeof LIBRARY_LABEL_TEXTS)[number];
export const LIBRARY_LID_STATES = ["closed", "ajar", "open"] as const;
export type LibraryLidState = (typeof LIBRARY_LID_STATES)[number];

/**
 * The tin's size, the one spec every prompt or check text on the platform states (the product page: a thin,
 * flat slide-lid tin about 9 by 5 cm; the lid slides along the long side). The real tin in a hand
 * (`tin-closed-inhand-real`, role `scale`) is the authority on scale; this is how it is said in words.
 */
export const TIN_SIZE_CM = { long: 9, short: 5 } as const;
export const TIN_SIZE_TEXT = `about ${TIN_SIZE_CM.long} x ${TIN_SIZE_CM.short} cm, thin and flat, the lid sliding along the long side` as const;

/**
 * The named default product reference sets, by how the tin is shown: `both`
 * (the usual default: the tin open and closed), `closed` and `open`. Each is
 * an ordered list strategists and admins keep in the library's Products tab;
 * the composer's product button attaches one (Both unless the editor picks
 * another). `both` is stored as `default_position`, the order every reader
 * had before the sets, so "the defaults" still means Both.
 */
export const LIBRARY_DEFAULT_SETS = ["both", "closed", "open"] as const;
export type LibraryDefaultSet = (typeof LIBRARY_DEFAULT_SETS)[number];
export const DEFAULT_LIBRARY_SET: LibraryDefaultSet = "both";

export interface LibraryImage {
  id: number;
  key: string;
  sha256: string;
  contentType: string;
  width: number | null;
  height: number | null;
  kind: string;
  /** The description: the source of the AI context, never sent raw. */
  notes: string;
  roles: LibraryRole[];
  labelText: LibraryLabelText;
  lidState: LibraryLidState | null;
  /** `refDescription` of this image: what the AI is told. */
  assetMapLine: string;
  /** A picture, or a reference video (a clip's `@Video n`; never sent to a frame). */
  mediaKind: LibraryMediaKind;
  /** A video's length, when known. */
  durationMs: number | null;
  /**
   * Its place among the default product references (1..N), which "Add product
   * references" attaches in this order; null when it is not one. Strategists
   * and admins set it; everyone sees it.
   */
  defaultPosition: number | null;
  /** Its place in each named default set (1..N), present only for the sets it is in; `both` equals `defaultPosition`. */
  defaultSets: Partial<Record<LibraryDefaultSet, number>>;
  addedBy: { userId: number | null; name: string | null };
  addedAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export type LibraryMediaKind = "image" | "video";

/** A library video: mp4, mov or webm, up to 100 MB (a picture stays at 25 MB). */
export const LIBRARY_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const;
export const LIBRARY_VIDEO_MAX_BYTES = 100 * 1024 * 1024;

/** What a library row's bytes are, by stored type: a video, else a picture. */
export const libraryMediaKindOf = (contentType: string | null | undefined): LibraryMediaKind => (referenceKindOfType(contentType) === "video" ? "video" : "image");

type DefaultSetItem = Pick<LibraryImage, "defaultPosition" | "archivedAt" | "id"> & { defaultSets?: Partial<Record<LibraryDefaultSet, number>> };

/** An item's place in one default set, or null when it is not in it. `both` reads `defaultPosition`. */
export function libraryDefaultPosition(item: DefaultSetItem, set: LibraryDefaultSet = DEFAULT_LIBRARY_SET): number | null {
  if (set === "both") return item.defaultPosition ?? null;
  const n = item.defaultSets?.[set];
  return typeof n === "number" && n >= 1 ? n : null;
}

/** One default set's items (Both when no set is named), live ones only, in their order. */
export function libraryDefaults<T extends DefaultSetItem>(library: readonly T[], set: LibraryDefaultSet = DEFAULT_LIBRARY_SET): T[] {
  return library
    .filter((item) => item.archivedAt === null && libraryDefaultPosition(item, set) !== null)
    .sort((a, b) => libraryDefaultPosition(a, set)! - libraryDefaultPosition(b, set)! || a.id - b.id);
}

/**
 * The set the product button attaches: the one asked for (Both by default)
 * while it holds anything, else Both, else the first set that does; null
 * when no set holds anything yet.
 */
export function resolveDefaultSet(requested: LibraryDefaultSet | null | undefined, counts: Record<LibraryDefaultSet, number>): LibraryDefaultSet | null {
  const want = requested ?? DEFAULT_LIBRARY_SET;
  if (counts[want] > 0) return want;
  if (counts[DEFAULT_LIBRARY_SET] > 0) return DEFAULT_LIBRARY_SET;
  return LIBRARY_DEFAULT_SETS.find((set) => counts[set] > 0) ?? null;
}

/** How many live items each default set holds. */
export function libraryDefaultCounts(library: readonly DefaultSetItem[]): Record<LibraryDefaultSet, number> {
  return Object.fromEntries(LIBRARY_DEFAULT_SETS.map((set) => [set, libraryDefaults(library, set).length])) as Record<LibraryDefaultSet, number>;
}

/**
 * The default order after one item moves: `ids` is the current order, `id`
 * goes to `position` (1-based, clamped to the end) or leaves it (null).
 * Pure, so the server's renumbering is tested without a database.
 */
export function placeDefault(ids: readonly number[], id: number, position: number | null): number[] {
  const rest = ids.filter((other) => other !== id);
  if (position === null) return rest;
  const at = Math.min(Math.max(1, Math.round(position)), rest.length + 1) - 1;
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/** Whether a clip model takes reference videos at all; a frame never does. */
export const clipTakesVideo = (model: ClipModel): boolean => CLIP_MEDIA_LIMITS[model].video > 0;

/** What "Add product references" attaches, from the defaults, given what the prompt has and has room for. */
export interface ProductReferencePlan<T> {
  images: T[];
  videos: T[];
  /** Already attached (by bytes): skipped. */
  already: number;
  /** Videos the model cannot take at all (a frame, or a clip model without reference videos). */
  unsupported: number;
  /** Left out because the prompt is at the model's cap for that kind. */
  noRoom: number;
}

/**
 * `room` is how many more of each kind the prompt may take; a `videos` of
 * null means the model takes none at all (a frame, or a clip model without
 * reference videos), so its videos count as `unsupported`, never as no room.
 */
export function planProductReferences<T extends Pick<LibraryImage, "sha256" | "mediaKind">>(
  defaults: readonly T[],
  have: { images: ReadonlySet<string>; videos: ReadonlySet<string> },
  room: { images: number; videos: number | null },
): ProductReferencePlan<T> {
  const plan: ProductReferencePlan<T> = { images: [], videos: [], already: 0, unsupported: 0, noRoom: 0 };
  for (const item of defaults) {
    const video = item.mediaKind === "video";
    const list = video ? plan.videos : plan.images;
    if ((video ? have.videos : have.images).has(item.sha256) || list.some((other) => other.sha256 === item.sha256)) {
      plan.already++;
      continue;
    }
    const cap = video ? room.videos : room.images;
    if (cap === null) plan.unsupported++;
    else if (list.length >= cap) plan.noRoom++;
    else list.push(item);
  }
  return plan;
}

/** The SOP sets an admin keeps (`generation_settings.product_sop`): library keys, in order. */
export interface ProductSop {
  closed: string[];
  open: string[];
  pellet: string[];
  handOpening: string | null;
  labelRule: string | null;
}

/* --------------------------------------------------------------------- admin */

export interface GenerationSettingsView {
  monthlyCapUsd: number;
  productSop: ProductSop;
  priceOverrides: PriceOverrides;
  updatedAt: string;
  updatedBy: { userId: number | null; name: string | null };
}

/** `GET /api/admin/generation/spend` (contract 2.5): live, paid outputs not failed or cancelled (plus fake ones off production, `includesTests`). */
export interface GenerationSpend {
  totalUsd: number;
  cap: { capUsd: number; thisMonthUsd: number };
  series: Array<{ start: string; usd: number }>;
  byPerson: Array<{ userId: number; name: string | null; usd: number; outputs: number }>;
  byModel: Array<{ model: string; usd: number; outputs: number }>;
  byKind: Array<{ kind: GenerationKind; usd: number; outputs: number }>;
  unknownCount: number;
  /** Off production on the fake provider, the totals and feed include fake runs, marked as tests. */
  includesTests: boolean;
}

export interface GenerationSpendItem {
  outputId: number;
  runId: number;
  kind: GenerationKind;
  model: string | null;
  status: GenerationOutputStatus;
  errorClass: GenerationErrorClass | null;
  by: { userId: number; name: string | null };
  at: string;
  estUsd: number | null;
  actualUsd: number | null;
  costSource: CostSource;
  storyboard: { id: number; title: string };
  brief: { id: number; title: string; ref: string } | null;
  /** `storyboard`: a prompt-chat frame or clip on no shot (key null). */
  owner: { kind: "shot" | "line" | "voiceover-b" | "storyboard"; key: string | null };
  /** The line the item belongs to (a shot's own line), named by place; `place` is null once the line is archived. */
  line: { key: string; place: { kind: "line" | "hook"; n: number } | null } | null;
  /** A fake run, shown off production only. It costs nothing real. */
  test: boolean;
  /** The re-voice batch it was read for (`revoice.ts`), or null for an ordinary Generate. */
  revoice: number | null;
  adopted: boolean;
  media: { thumb?: string; poster?: string; peaks?: string };
}

export interface GenerationSpendFeed {
  items: GenerationSpendItem[];
  next: string | null;
}

/* ------------------------------------------------- request bodies (the API's) */

/** `POST .../generations/:runId/outputs/:n/retry`. `confirmPayTwice` is required for an `unknown` output (admins only). */
export interface GenerationRetryInput {
  idempotencyKey: string;
  confirmPayTwice?: boolean;
}

/** `POST .../voiceover-a/rebuild`. */
export interface VoiceoverRebuildInput {
  idempotencyKey: string;
}

/** Every route of contract 2.3 takes the storyboard edit's `baseRevision` and an optional note, and answers `{ revision, changed }`. */
export interface GenerationEditBase {
  baseRevision?: number;
  note?: string | null;
}

/** `POST .../shots/:key/adopt`: an output or an older take, onto an option, as a new option, or as the shot's clip. */
export interface GenerationAdoptInput extends GenerationEditBase {
  outputId?: number;
  takeId?: number;
  into: { slotId: number } | "new" | "clip";
}

/** `POST .../shots/:key/make-main` and `.../remove-option`. */
export interface GenerationOptionInput extends GenerationEditBase {
  slotId: number;
}

/** `POST .../lines/:key/adopt-voice`. An upload or an editor's file always goes to the `upload` slot. */
export interface GenerationAdoptVoiceInput extends GenerationEditBase {
  outputId?: number;
  takeId?: number;
  pipelineFileId?: number;
  /** The slot the take goes to; must agree with the output's own voice when it was generated. */
  voiceAlias?: string;
  /** For an upload: the popup's text, kept on the take for reference. */
  text?: string;
}

/**
 * `POST .../voice-slots/:slotId/adopt`: a voiceover output (generated or
 * uploaded) or an earlier take of the same slot, put on show in one voiceover
 * slot - a free clip's, or a line's own (which the line then uses). Answers
 * `{ revision, changed, slotId, takeId }`; like every adopt from the clip
 * editor's sidebar, publish it with an empty `POST .../editor/changes`.
 */
export interface GenerationAdoptToSlotInput extends GenerationEditBase {
  outputId?: number;
  takeId?: number;
  /** For an upload: its text, kept on the take for reference. */
  text?: string;
}

/** `POST .../voiceover-a/adopt`. */
export interface VoiceoverAAdoptInput extends GenerationEditBase {
  outputId: number;
}

/** `POST .../voiceover-b/adopt`. */
export interface VoiceoverBAdoptInput extends GenerationEditBase {
  outputId?: number;
  takeId?: number;
}

/** `POST|DELETE .../gallery/hides`. `owner.kind: 'storyboard'` is Voiceover B's gallery. */
export interface GalleryHideInput {
  owner: GenerationOwner;
  sha256: string;
}

export interface GalleryHideResult {
  hidden: boolean;
}

/** `PUT .../uploads/:sha256?kind=` answers this. A `reference` upload also carries its row. */
export type GenerationUploadKind = "frame" | "clip" | "audio" | "reference";

/** The largest reference video or audio a prompt takes (a reference picture stays at 25 MB). */
export const REFERENCE_MEDIA_MAX_BYTES = 300 * 1024 * 1024;

/** What a file is, as a reference: a picture, a video or audio, by its content type; null for anything else. */
export function referenceKindOfType(contentType: string | null | undefined): "image" | ClipMediaKind | null {
  const t = (contentType ?? "").split(";")[0]!.trim().toLowerCase();
  if (/^image\/(jpeg|png|webp)$/.test(t)) return "image";
  if (/^video\/(mp4|quicktime|webm)$/.test(t)) return "video";
  if (/^audio\/(mpeg|mp3|wav|x-wav|wave|mp4|x-m4a|m4a|aac|ogg|webm|flac)$/.test(t)) return "audio";
  return null;
}

export interface GenerationUploadResult {
  sha256: string;
  bytes: number;
  contentType: string;
  alreadyStored: boolean;
  reference: StoryboardReferenceUpload | null;
}

/** A reference picture uploaded into a prompt ("Uploaded here" in the picker). */
export interface StoryboardReferenceUpload {
  id: number;
  sha256: string;
  name: string;
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  /** `/api/pipeline/storyboards/:id/reference-uploads/:sha256`; `/thumb` beside it. */
  url: string;
  uploadedBy: { userId: number | null; name: string | null };
  createdAt: string;
}

/** One `ref_image` slot the picker's This-presentation tab lists (library not `brand`). */
export interface StoryboardReferenceImage {
  slotId: number;
  key: string;
  /** `ref_image/<key>`, the slot ref an input names it by. */
  ref: string;
  meta: Record<string, unknown>;
  takeId: number | null;
  sha256: string | null;
  contentType: string | null;
  width: number | null;
  height: number | null;
  /** `refDescription` of the slot's meta: the ASSET MAP line it would get. */
  assetMapLine: string;
}

/** `GET .../references`. */
export interface StoryboardReferencesResult {
  refImages: StoryboardReferenceImage[];
  uploads: StoryboardReferenceUpload[];
}

/** An editor's own audio for one line: a brief file bound to the line, laid over it in every read. */
export interface LineAudioFile {
  id: number;
  name: string;
  contentType: string;
  bytes: number;
  durationMs: number | null;
  /** `/api/pipeline/files/:id`, the brief file route. */
  url: string;
  uploadedBy: { userId: number | null; name: string | null; role: string | null };
  createdAt: string;
}

/** `GET .../lines/:key/gallery` also lists the line's editor audio, for "Use as the voiceover". */
export interface LineGalleryResult extends GalleryResult {
  editorAudio: LineAudioFile[];
}

/* ------------------------------------------------------------- voices (API) */

/** `POST /generation/voices`. */
export interface VoiceAddInput {
  voiceId: string;
  alias?: string;
  note?: string | null;
}

/** What an add answers when it saved (201) or found the id already listed (409, `duplicate`). */
export interface VoiceAddResult {
  code: VoiceCheckCode;
  voice: GenerationVoice | null;
  /** The i18n key of the Voice Library warning, when the voice is a library voice. */
  warning: string | null;
}

/**
 * `GET /generation/voices/lookup/:voiceId`: what ElevenLabs says about an id,
 * nothing saved. `duplicate` names the voice already listed in `listed`.
 */
export interface VoiceLookupResult {
  code: VoiceCheckCode;
  voiceId: string;
  /** The voice's ElevenLabs name, when found. */
  name: string | null;
  labels: Record<string, string>;
  /** A Voice Library voice (it may cost more and can vanish). */
  library: boolean;
  /** Whether `voices.lookupPreviewUrl` has a sample to play. */
  hasPreview: boolean;
  listed: GenerationVoice | null;
}

/**
 * `POST /storyboards/:id/voices/resolve`: the team's voice for a storyboard's
 * own voice alias (an agent's), added to the list when the storyboard's takes
 * name a real ElevenLabs voice. `voice` is null when it cannot be: `unknown`
 * (the storyboard names no voice id for it) or the check's refusal.
 */
export interface VoiceResolveResult {
  alias: string;
  code: "found" | "unknown" | Exclude<VoiceCheckCode, "found" | "duplicate">;
  voice: GenerationVoice | null;
  /** Added to the team's list by this call. */
  added: boolean;
}

/** `PATCH /generation/voices/:id`. `ifUpdatedAt` guards an undo (contract 8.5). */
export interface VoicePatchInput {
  name?: string;
  note?: string | null;
  archived?: boolean;
  ifUpdatedAt?: string;
}

/* ------------------------------------------------------------ library (API) */

/** `POST /generation/library`: bytes already at the global prefix (`sha256`), or an image this storyboard holds. */
export interface LibraryAddInput {
  sha256?: string;
  from?: { storyboardId: number; sha256: string };
  /** A video's length as the browser measured it (the worker fills it in when it can read one). */
  durationMs?: number | null;
  notes: string;
  roles: LibraryRole[];
  labelText: LibraryLabelText;
  lidState?: LibraryLidState | null;
  key?: string;
}

/** `PATCH /generation/library/:id`. */
export interface LibraryPatchInput {
  notes?: string;
  roles?: LibraryRole[];
  labelText?: LibraryLabelText;
  lidState?: LibraryLidState | null;
  archived?: boolean;
  /** Strategists and admins: make it number n (clamped to the end) of `defaultSet`, or take it out of that set (null). */
  defaultPosition?: number | null;
  /** The default set `defaultPosition` places it in; Both when left out. */
  defaultSet?: LibraryDefaultSet;
  ifUpdatedAt?: string;
}

/**
 * A call step's refusal (contract 8.5): the row moved since the step's
 * forward answer. The undo route's `UndoCallBlocker`, one type.
 */
export type GenerationCallBlocker = UndoCallBlocker;

/* -------------------------------------------------------------- admin (API) */

/** `PATCH /api/admin/generation/settings`. */
export interface GenerationSettingsPatch {
  monthlyCapUsd?: number;
  productSop?: ProductSop;
  priceOverrides?: PriceOverrides;
}

/* ------------------------------------------ remembered composer settings */

/**
 * The composer settings a person last chose, kept on their account
 * (`users.generation_prefs`) so the next composer opens with them on any
 * concept and any device, until they choose otherwise. Every field is
 * optional: one never chosen falls back to the composer's own default.
 * `GET /generation/prefs`, `PATCH /generation/prefs` (merged, per section).
 */
export interface GenerationPrefs {
  clip?: { model?: ClipModel; resolution?: ClipResolution; durationS?: number; generateAudio?: boolean };
  frame?: { count?: number };
}

/** A clip composer before its person chooses anything: `CLIP_DEFAULT`, for every role. */
export const EDITOR_CLIP_DEFAULT: { model: ClipModel; resolution: ClipResolution } = CLIP_DEFAULT;

/**
 * A stored preference held to what the models take today: an unknown model
 * or resolution, a length outside the range or a count outside 1-4 is
 * dropped (the default stands), never sent.
 */
export function cleanGenerationPrefs(raw: unknown): GenerationPrefs {
  const out: GenerationPrefs = {};
  const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
  const clip = obj(obj(raw)?.clip);
  if (clip) {
    const c: NonNullable<GenerationPrefs["clip"]> = {};
    if ((CLIP_MODELS as readonly unknown[]).includes(clip.model)) c.model = clip.model as ClipModel;
    if ((CLIP_RESOLUTIONS as readonly unknown[]).includes(clip.resolution)) c.resolution = clip.resolution as ClipResolution;
    if (typeof clip.durationS === "number" && Number.isInteger(clip.durationS) && clip.durationS >= 1 && clip.durationS <= 60) c.durationS = clip.durationS;
    if (typeof clip.generateAudio === "boolean") c.generateAudio = clip.generateAudio;
    if (Object.keys(c).length > 0) out.clip = c;
  }
  const frame = obj(obj(raw)?.frame);
  if (frame && typeof frame.count === "number" && Number.isInteger(frame.count) && frame.count >= FRAME_COUNT_RANGE.min && frame.count <= FRAME_COUNT_RANGE.max) {
    out.frame = { count: frame.count };
  }
  return out;
}

/* ------------------------------------------- round 3: galleries (D3) (API) */

/**
 * `GET /api/pipeline/storyboards/:id/gallery[?line=<key>][&mine=1][&hidden=1]`:
 * everything made for a concept (grouped by line, then shot), or for one line
 * (`?line=`; `looseShots` is then empty). Each item carries `kind`, `by` and
 * `onSlot`, so the web filters All / Frames / Videos / Voiceovers and Mine /
 * Everyone itself; `mine=1` filters on the server. Strategists, admins, and
 * the concept's assigned editor while it is in `EDITOR_WORK_STAGES`.
 */
export interface MediaGalleryResult {
  storyboardId: number;
  brief: { id: number; ref: string; title: string } | null;
  lines: MediaGalleryLine[];
  /** Shots that belong to no line (never filled on a `?line=` read). */
  looseShots: MediaGalleryShot[];
  /**
   * "From chat, not on a shot": the frames and clips the prompt chat made for
   * the concept with no target shot (runs with no shot and no line). Empty on
   * a `?line=` read. "Use on Shot N" adopts them like any output.
   */
  fromChat: MediaGalleryChat;
  /** Every run an item came from, with its full request (prompt, inputs, settings) and `requestedBy`. */
  runs: GenerationRunView[];
  /** Everyone with an item here, for the filter's names. */
  people: GalleryAuthor[];
}

export interface MediaGalleryLine {
  key: string;
  kind: string;
  position: number;
  text: string;
  /** The line's voiceover gallery: generated takes, the agent's, uploads. */
  voiceovers: GalleryItem[];
  /** The editor's own audio on the line (brief files). */
  editorAudio: LineAudioFile[];
  shots: MediaGalleryShot[];
}

export interface MediaGalleryChat {
  frames: GalleryItem[];
  clips: GalleryItem[];
}

export interface MediaGalleryShot {
  key: string;
  position: number;
  line: string | null;
  frames: GalleryItem[];
  clips: GalleryItem[];
}

/* -------------------------------------------- round 3: training data (D5) */

/** Why a slot's current media changed, as a `generation_selections` row records it. */
export const SELECTION_VIAS = ["adopt", "make_main", "remove_option", "adopt_voice", "voiceover_a", "voiceover_b", "patch", "archive", "restore", "undo", "redo", "discard", "agent_publish", "agent_edit", "finalize"] as const;
export type SelectionVia = (typeof SELECTION_VIAS)[number];

/** One pick or unpick of a slot's current media (`generation_selections`), as the export writes it. */
export interface GenerationSelectionView {
  id: number;
  at: string;
  event: "pick" | "unpick";
  via: SelectionVia | string;
  storyboardRevision: number;
  by: GalleryAuthor & { viaAgent: boolean };
  take: { id: number; sha256: string | null; runId: number | null; outputId: number | null; prompt: string | null; model: string | null } | null;
  previous: { id: number; sha256: string | null } | null;
  original: { id: number; sha256: string | null } | null;
}

/** A file in the export: the stable storage path, and a signed URL when asked for. */
export interface ExportFile {
  storageKey: string;
  sha256: string;
  contentType: string | null;
  bytes: number | null;
  url: string | null;
}

/**
 * One line of `generation:export` / `GET /api/admin/generation/export`
 * (JSONL): one slot of one storyboard, with what the agent first put there,
 * every generation made for it, every pick in order, and the final pick at
 * each publish and at approval.
 */
export interface GenerationExportRecord {
  schema: "scandi.generation-export.v1";
  storyboard: { id: number; slug: string; title: string };
  concept: { id: number; ref: string; title: string; stage: string; approvedAt: string | null } | null;
  slot: { id: number; kind: string; key: string; shot: string | null; line: string | null; archived: boolean; label: string | null };
  /** The agent's first take on this slot, with its prompt and file; null for a slot a person made. */
  original: { takeId: number; prompt: string | null; params: Record<string, unknown>; model: string | null; file: ExportFile | null } | null;
  generations: Array<{
    runId: number;
    outputId: number;
    n: number;
    kind: string;
    status: string;
    prompt: string | null;
    authored: string | null;
    inputs: unknown[];
    model: string | null;
    settings: Record<string, unknown>;
    author: GalleryAuthor;
    createdAt: string;
    /** Where the click was made (`prompt_chat` for the prompt chat's cards); null before the ledger. */
    surface?: string | null;
    file: ExportFile | null;
    files: Array<ExportFile & { kind: string }>;
  }>;
  selections: GenerationSelectionView[];
  /** The take on show at each publish snapshot still kept, and at approval. */
  finalPicks: {
    publishes: Array<{ revision: number; at: string; reason: string; takeId: number | null; sha256: string | null; file: ExportFile | null }>;
    approval: { at: string; takeId: number | null; sha256: string | null; file: ExportFile | null } | null;
  };
  current: { takeId: number; sha256: string | null; file: ExportFile | null } | null;
}
